/**
 * «أداء الوحدات» — every word on the weekly performance page (tasks/property-performance-phase1.md step 5).
 *
 * ⚠️ ARABIC-ONLY, deliberately — like every ops page (config/stockLabels.js). The backend answers in
 * CODES (verdicts, metric keys, LISTING_PERF_xxx refusals); this file is the one place they become words.
 * The verdict words are the sheet's own («لوحة المتابعة» row 9), so the parallel run reads the same.
 */
import { LISTING_PERF_ERROR_CODES as C } from '../utils/listingPerformanceErrorCodes';

export const TEXT = {
  pageTitle: 'أداء الوحدات',
  pageSubtitle: 'القراءة الأسبوعية لكل وحدة: الإشغال أولاً، ثم الظهور والدخول والحجز على المنصات.',
  refresh: 'تحديث',
  loading: 'جارٍ التحميل…',
  unexpected: 'حدث خطأ غير متوقع. حاول مرة أخرى.',

  weekLabel: 'الأسبوع المنتهي في',
  latestWeek: 'آخر أسبوع',
  backToLatest: 'العودة إلى آخر أسبوع',
  noWeeks: 'لا توجد أسابيع مسجلة بعد. تظهر الأرقام بعد أول جمع أسبوعي أو بعد استيراد تاريخ الجدول.',
  noPlatformNumbers: 'لا أرقام من Airbnb لهذا الأسبوع — المعروض أدناه أرقام النظام (الإشغال والليالي والتقييمات).',
  bookingsOnlyAirbnb: 'الجدول القديم حفظ حجوزات Airbnb فقط',
  lastCollected: 'آخر جمع',
  lastCollectedNone: 'لم يصل أي جمع بعد',
  by: 'بواسطة',
  ownerView: 'عرض المالك',
  ownerViewHint: 'أرقام واتجاهات وحدات هذا المالك فقط — بدون القراءة أو الحدود أو الملاحظات.',
  ownerViewNeedsOwner: 'اختر المالك من القائمة أعلاه لعرض صفحة المالك.',

  tabs: { airbnb: 'Airbnb', bookingcom: 'Booking.com', all: 'كل المنصات', settings: 'الإعدادات' },
  tabLevel: {
    airbnb: 'لكل وحدة · الإشغال من النظام (كل المنصات)',
    bookingcom: 'لكل عقار على Booking.com · يتأخر أسبوعاً',
    all: 'الإشغال والحجوزات والتقييمات من النظام · لكل وحدة'
  },

  // Board columns
  unit: 'الوحدة',
  verdict: 'القراءة',
  occupancy: 'الإشغال',
  occupancyAllPlatforms: 'الإشغال (كل المنصات)',
  collection: 'وصول الأرقام',
  collectionHelp: 'هل وصلت أرقام هذا الأسبوع من الجمع الأسبوعي (يوم الاثنين)؟ ✓ = وصلت من كل المنصات',
  experiment: 'تجربة منذ',
  experimentTag: 'تجربة',
  notRead: 'لم يُقرأ',

  // Board — column groups and short headers (the full name is the header's hover text)
  groups: { nights: 'ليالي الأسبوع', week: 'هذا الأسبوع', occupancy: 'الإشغال', funnel: 'من الظهور إلى الحجز (Airbnb)', quality: 'التقييم والسعر', calendar: 'الإشغال والتقويم' },
  short: {
    impressions: 'الظهور', pageViews: 'الدخول', entryRate: 'نسبة الدخول', newBookings: 'حجوزات جديدة',
    rating: 'التقييم', bookedNightRate: 'سعر الليلة'
  },
  full: {
    impressions: 'الظهور في الصفحة الأولى — مرات ظهور الإعلان في أول صفحة بحث',
    pageViews: 'الدخول على صفحة الوحدة',
    entryRate: 'نسبة الدخول على صفحة الوحدة من مجموع الظهور',
    newBookings: 'حجوزات Airbnb التي تمت هذا الأسبوع (بتاريخ الحجز، لا الإقامة)',
    rating: 'تقييم Airbnb العام كما يظهر للضيف',
    bookedNightRate: 'متوسط سعر الليالي المحجوزة عبر Airbnb هذا الأسبوع (ر.س)'
  },
  allReceived: 'وصلت أرقام كل المنصات',
  // Glance strip
  glanceAll: 'الكل',
  glanceAttention: 'تحتاج مراجعة',
  glanceHint: 'اضغط حالة لعرض وحداتها فقط',
  // Detail
  why: 'لماذا',
  runningExperiment: 'تجربة جارية',
  reviewOn: 'المراجعة',
  showAllChanges: 'عرض كل السجل',
  showFewerChanges: 'عرض آخر التغييرات فقط',
  more: 'المزيد',
  less: 'أقل',
  changesInWeek: 'تغيير في هذا الأسبوع',
  noRows: 'لا توجد وحدات لهذا المالك.',
  openDetail: 'عرض تفاصيل الوحدة',

  // Unit detail
  close: 'إغلاق',
  thisWeek: 'هذا الأسبوع',
  priorWeek: 'الأسبوع السابق',
  fourWeekAvg: 'متوسط 4 أسابيع',
  metric: 'المقياس',
  occupancyChart: 'الإشغال — آخر 8 أسابيع',
  targetLabel: 'الهدف',
  changeKey: 'تغيير مسجّل في سجل التغييرات',
  reviewsChart: 'نجوم تقييمات Airbnb الجديدة — آخر 8 أسابيع',
  reviewsChartNote: 'متوسط نجوم التقييمات التي وصلت ذلك الأسبوع. التقييم العام للوحدة في الجدول أدناه.',
  noReview: 'لا تقييم جديد',
  // The key names AVERAGE bands — a bar is coloured by the week's average stars, not by one review.
  stars5: 'متوسط 5',
  stars4: 'من 4 إلى أقل من 5',
  stars3: 'أقل من 4',
  avgStars: 'المتوسط',
  notes: 'ملاحظات القراءة',
  readOn: 'قُرئت في',

  // Booking.com
  property: 'العقار',
  bdcWeekly: 'قراءة أسبوعية مقابل الوحدات المنافسة',
  bdcMonthly: 'قراءة شهرية مقابل الوحدات المنافسة',
  bdcFellBack: 'أرقام Booking.com لهذا الأسبوع لم تُنشر بعد — المعروض أحدث أسبوع قبله.',
  competitors: 'الوحدات المنافسة',
  thisMonth: 'هذا الشهر',
  priorMonth: 'الشهر السابق',
  noBookingcom: 'لا يوجد عقار Booking.com مربوط بوحدات هذا المالك.',

  // All platforms
  bookedNights: 'محجوزة',
  openNights: 'متاحة',
  blockedNights: 'مغلقة',
  unknownNights: 'غير معروفة',
  bookingsMadeShort: 'حجوزات جديدة',
  newReviewsShort: 'تقييمات Airbnb',
  conflictHint: 'ليالٍ معروضة للبيع وعليها حجز — تحتاج مراجعة التقويم',

  // Change log
  changeLog: 'سجل التغييرات',
  changeLogHint: 'كل تغيير على الإعلان أو السعر أو التقويم. التجربة = تغيير له فرضية وتاريخ مراجعة.',
  showChangeLog: 'عرض سجل التغييرات',
  hideChangeLog: 'إخفاء سجل التغييرات',
  addChange: 'إضافة تغيير',
  editChange: 'تعديل التغيير',
  noChanges: 'لا تغييرات مسجلة لهذه الوحدة.',
  save: 'حفظ',
  cancel: 'إلغاء',
  optional: 'اختياري',
  // Read by screen readers beside a required group's asterisk (the platform pick has no single input to mark).
  required: 'مطلوب',
  moreDetails: 'تفاصيل إضافية',
  changeFields: {
    date: 'التاريخ',
    platforms: 'المنصة',
    change: 'التغيير',
    hypothesis: 'الفرضية',
    settingBefore: 'الإعداد قبل',
    settingAfter: 'الإعداد بعد',
    guestPriceBefore: 'سعر الضيف قبل',
    guestPriceAfter: 'سعر الضيف بعد',
    otherFactors: 'عوامل أخرى في نفس الفترة',
    reviewDate: 'تاريخ المراجعة',
    result: 'النتيجة'
  },
  importedFromSheet: 'من الجدول',
  reviewDateHint: 'تُحسب الأرقام حتى الليلة السابقة لهذا التاريخ. فارغ = 14 ليلة من تاريخ التغيير.',
  // The platform pick (tasks/change-impact-ai-step4.md step 1b): one change per row (founder 2 Nov).
  platformsHint: 'تغيير واحد في كل سطر. اختر أكثر من منصة فقط إذا طُبّق التغيير نفسه عليها كلها.',

  // The counted box on a change (tasks/change-impact-before-after.md). It only counts: no word here may imply that
  // the change caused anything, and it is never called «النتيجة», which stays ops' own conclusion.
  impactTitle: 'الأرقام حول هذا التغيير',
  impactNote: 'أرقام فقط، ولا تثبت أن التغيير هو السبب',
  impactBefore: 'قبل التغيير',
  impactAfter: 'من يوم التغيير',
  impactRangeTo: 'إلى',
  impactSold: 'ليالٍ مباعة',
  impactBookedBefore: 'منها حُجز قبل التغيير',
  impactClosed: 'ليالٍ مغلقة',
  impactUnknown: 'ليالٍ بلا سجل',
  impactOtherChanges: 'تغييرات أخرى في الفترة',
  impactReadyOn: 'الأرقام جاهزة يوم',
  resultsDue: 'نتائج لم تُكتب',
  resultNotWritten: 'النتيجة لم تُكتب',
  writeResult: 'اكتب النتيجة',

  // Settings — thresholds
  thresholdsTitle: 'حدود القراءة',
  thresholdsHint: 'تُطبَّق على الأسابيع التي تنتهي بعد الحفظ. الأسابيع السابقة تبقى بالحدود التي قُرئت بها.',
  thresholdsAdminOnly: 'تعديل الحدود للمدير فقط.',
  reason: 'سبب التغيير (اختياري)',
  saveThresholds: 'حفظ الحدود',
  savedFrom: 'سيُطبَّق ابتداءً من الأسبوع المنتهي في',
  pendingLead: 'حدود جديدة تبدأ من الأسبوع المنتهي في',
  pendingTail: 'الأسابيع السابقة تبقى مقروءة بحدودها.',
  thresholdShort: {
    airbnbImpressionsMin: 'حد الظهور',
    airbnbEntryRateMin: 'حد نسبة الدخول',
    airbnbSimilarViewsMargin: 'هامش الوحدات المشابهة',
    airbnbMinPageViews: 'حد أدلة الحجز',
    occupancyTarget: 'هدف الإشغال',
    occupancyAmberMargin: 'هامش البرتقالي',
    bookingcomCompMargin: 'هامش المقارنة (Booking.com)',
    bookingcomSearchAvailabilityMin: 'حد توافر البحث'
  },
  lastChange: 'آخر تغيير',
  noChangeYet: 'قيم الجدول الأصلية — لم تتغير بعد',
  showHistory: 'عرض السجل',
  hideHistory: 'إخفاء السجل',
  version: 'النسخة',
  initialValues: 'قيم الجدول الأصلية',
  thresholdLabels: {
    airbnbImpressionsMin: 'حد الظهور الأسبوعي في الصفحة الأولى (أقل منه = ضعف الظهور)',
    airbnbEntryRateMin: 'حد نسبة الدخول على صفحة الوحدة % (أقل منه = ضعف الدخول)',
    airbnbSimilarViewsMargin: 'هامش المقارنة مع الوحدات المشابهة (Airbnb)',
    airbnbMinPageViews: 'أقل عدد دخول في آخر 4 أسابيع للحكم على الحجز (Airbnb)',
    occupancyTarget: 'حد الإشغال الأسبوعي % (عنده أو فوقه = أخضر)',
    occupancyAmberMargin: 'هامش البرتقالي — نقاط تحت حد الإشغال',
    bookingcomCompMargin: 'Booking.com: هامش المقارنة مع المنافسين',
    bookingcomSearchAvailabilityMin: 'Booking.com: حد توافر البحث %'
  },
  // One line under each box that may stay empty — what the value does, and that empty switches it off.
  thresholdHints: {
    airbnbSimilarViewsMargin: 'الدخول أقل من الوحدات المشابهة × الهامش = ضعف (0.9 = أقل بأكثر من 10%). اتركه فارغاً لإيقاف القاعدة.',
    airbnbMinPageViews: 'لا حجز في آخر 4 أسابيع رغم هذا العدد أو أكثر = «ضعف التحويل»؛ أقل منه = «أدلة غير كافية». اتركه فارغاً لإيقاف القاعدة.'
  },
  thresholdOff: 'متوقف',

  // «تنبيه شارة» — beside the verdict when the unit lost Guest Favourite this week (rules.guestFavouriteLost).
  badgeLost: 'تنبيه شارة',
  badgeLostHint: 'فقدت الوحدة شارة «مفضلة لدى الضيوف» هذا الأسبوع — اقرأ آخر تقييم منخفض',
  // The unit page gives the badge its own line, so the verdict's «لماذا» never reads as the badge's reason.
  badgeLostLine: 'فقدت الوحدة «مفضلة لدى الضيوف» هذا الأسبوع — اقرأ آخر تقييم منخفض وعالج سببه',

  // Settings — keys
  keysTitle: 'مفاتيح الإرسال',
  keysHint: 'لكل شخص يجمع الأرقام كل اثنين مفتاح خاص. المفتاح يرسل الأرقام فقط، ويُعرض مرة واحدة عند إنشائه.',
  keyName: 'اسم المفتاح',
  keyNamePlaceholder: 'مثال: حاسوب فهد',
  keyHolder: 'صاحب المفتاح',
  createKey: 'إنشاء مفتاح',
  keyShownOnce: 'انسخ المفتاح الآن واحفظه في مكان آمن على جهاز الجمع — لن يظهر مرة أخرى.',
  copy: 'نسخ',
  copied: 'نُسخ',
  done: 'تم',
  revoke: 'إلغاء المفتاح',
  revokeConfirm: 'يتوقف المفتاح فوراً ولا يمكن إرجاعه.',
  revokeYes: 'نعم، ألغِ المفتاح',
  revoked: 'ملغى',
  created: 'أُنشئ',
  lastUsed: 'آخر استخدام',
  uses: 'مرات الاستخدام',
  neverUsed: 'لم يُستخدم',
  noKeys: 'لا توجد مفاتيح بعد.',
  uploads: 'وصول الأرقام الأسبوعية',
  uploadsHint: 'كل مرة أُرسلت فيها أرقام الأسبوع إلى النظام، ومن أرسلها، وماذا وصل.',
  lastUpload: 'آخر وصول',
  hideUploads: 'إخفاء',
  showRecentUploads: 'عرض آخر',
  noUploads: 'لم تصل أرقام بعد.',
  uploadsError: 'تعذّر تحميل سجل وصول الأرقام.',
  thresholdsIncomplete: 'املأ الحدود بأرقام قبل الحفظ — وحدها حدود الوحدات المشابهة يمكن أن تبقى فارغة.',
  receivingOff: 'استقبال الأرقام متوقف على هذا الخادم حتى يُشغَّل.',
  skillVersion: 'نسخة طريقة الجمع',
  versionShort: 'نسخة'
};

/** The sheet's verdict words, by the backend's codes. */
/**
 * «قراءة الأسبوع» — the AI brief and the collection notes in one card (tasks/unit-performance-ai-brief.md
 * step 5). The server sends codes (status, market, action kind, notices, missing figures by the page's keys);
 * these are their words. The AI's own text is shown as it came, never translated here.
 */
export const BRIEF = {
  card: 'قراءة الأسبوع',
  ai: 'قراءة المساعد الذكي',
  aiHint: 'اقتراح من المساعد الذكي يقرأ أرقام الأسبوع فقط — القرار لك',
  week: 'أسبوع',
  dataReadAt: 'الأرقام مستلمة في',
  suggestion: 'الاقتراح',
  actions: { listing: 'الإعلان', price: 'السعر', none: 'لا إجراء' },
  // The system lock (step 4): waiting for a change's result is its own answer — never shown as «لا إجراء».
  waitChip: 'انتظر',
  waitLine: (date, readyOn) => `انتظر نتيجة تغيير ${date} قبل أي تغيير في السعر — تظهر يوم ${readyOn}`,
  // The pair reads as one control: «عرض الأسباب» / «إخفاء الأسباب» (founder, 22 Oct — «لماذا؟» was vague).
  why: 'عرض الأسباب',
  hideWhy: 'إخفاء الأسباب',
  // On a card with the strip (layer-2 build): only the AI's own reasoning is folded here — the rest is above.
  whySuggestion: 'لماذا هذا الاقتراح؟',
  hideWhySuggestion: 'إخفاء',
  aiTag: 'من المساعد الذكي',
  questions: {
    unitMoved: 'هل تغيّر أداء الوحدة؟',
    marketMoved: 'هل تغيّر السوق؟',
    reviewsOrBadges: 'هل تغيّر تقييم أو شارة؟',
    changesOrCalendar: 'هل غيّرنا شيئاً أو غيّرت المنصة شيئاً؟'
  },
  gaps: 'ما ينقص القراءة',
  notices: {
    check_floor: 'تحقق من الحد الأدنى للوحدة قبل التطبيق',
    built_on_earlier_data: 'مبني على نسخة سابقة من بيانات الأسبوع — وصلت أرقام أحدث بعد المراجعة',
    missing_data: 'لا قراءة هذا الأسبوع — أرقام ناقصة',
    // Why a reading failed (step 4 — the incident of 2 Nov: every card read «تعذّرت» and nobody knew the AI account had
    // no credit). One per failed card; the server sends the code, never the error.
    failed_out_of_credit: 'السبب: نفد رصيد خدمة الذكاء الاصطناعي. بعد شحنه اضغط «إعادة القراءة» — لا تُحسب محاولة.',
    failed_key: 'السبب: مفتاح خدمة الذكاء الاصطناعي مرفوض أو غير مضبوط — يلزم إصلاحه من الإدارة.',
    failed_answer_refused: 'السبب: لم تجتز إجابة المساعد فحوص النظام، فلم تُعرض.',
    failed_data: 'السبب: تعذّر تجهيز أرقام الوحدة — تُعاد المحاولة مع التحديث القادم.',
    failed_provider: 'السبب: لم تستجب خدمة الذكاء الاصطناعي — تُعاد المحاولة مع التحديث القادم.',
    // A permanent refusal (a retired model, a bad request) never retries by itself (audit, 3 Nov).
    failed_needs_admin: 'السبب: رفضت خدمة الذكاء الاصطناعي الطلب — يلزم تدخل من الإدارة، ولن تُعاد المحاولة تلقائياً.',
    // Founder's wording, 21 Oct — shown in place of a price idea the data cannot support.
    no_price_comparison: 'لا يوجد اقتراح سعر — لا توجد مقارنة سعر لهذه الوحدة. للمراجعة، قارن السعر يدوياً بالوحدات المشابهة',
    // brief-v11 — a comparison exists this week but cannot carry a price idea (uncertain, too thin, «أدلة محدودة»).
    weak_price_comparison: 'لا يوجد اقتراح سعر — المقارنة بالوحدات المشابهة غير كافية هذا الأسبوع (التفاصيل في «هل تغيّر السوق؟»)',
    // brief-v11 — a solid comparison, but the unit is at or below similar units: no reason to review its price.
    market_not_above: 'لا يوجد اقتراح سعر — سعر الوحدة ليس أعلى من الوحدات المشابهة'
  },
  status: {
    none: 'لم تُكتب قراءة لهذا الأسبوع بعد.',
    pending: 'جارٍ كتابة القراءة — تظهر هنا خلال دقيقة تقريباً.',
    failed: 'تعذّرت كتابة القراءة هذا الأسبوع.'
  },
  rerun: 'إعادة القراءة',
  ask: 'هل تتفق مع الاقتراح؟',
  agree: 'أتفق',
  disagree: 'لا أتفق',
  reasonLabel: 'لماذا لا تتفق؟ (اختياري)',
  reasonPlaceholder: 'مثال: السبب الحقيقي هو إغلاق التقويم، لا السعر',
  save: 'حفظ',
  cancel: 'إلغاء',
  // The two lines the SYSTEM writes from its own figures (brief-v9): the market as observations, the reviews as facts.
  facts: {
    systemTag: 'من أرقام النظام',
    marketPrice: 'سعرنا مقابل الوحدات المشابهة',
    otherUnits: 'الوحدات الأخرى',
    sameBuilding: 'المبنى نفسه',
    moved: { up: 'ارتفعت', down: 'انخفضت', same: 'بدون تغيير' },
    vsSimilar: 'نسبة الحجز مقابل الوحدات المشابهة (الأسبوع الماضي)',
    vs: { above: 'أعلى', below: 'أقل', same: 'مثلها' },
    effect: 'أثر السوق',
    effects: { may_be_part: 'قد يكون جزءاً من السبب', unknown: 'غير معروف' },
    unavailable: 'غير متاح',
    newReviews: 'تقييمات جديدة',
    noNewReviews: 'لا تقييمات جديدة',
    avgStars: (stars) => `بمتوسط ${stars} نجوم`,
    rating: 'التقييم العام',
    ratingMoved: { up: 'ارتفع', down: 'انخفض', same: 'بدون تغيير' },
    badge: 'الشارة',
    badgeMoved: { same: 'بدون تغيير', changed: 'تغيّرت' },
    // Step 4 (tasks/change-impact-ai-step4.md): the past-result line the SYSTEM writes — the newest finished change,
    // only when its counted result carries a warning. One line naming every warning that applies; a limit, never a
    // verdict on the change. Keys = briefVocabulary.OUTCOME_WARNINGS (a parity test keeps them equal).
    // The changes line the SYSTEM writes (step 4, founder 3 Nov): this week's change-log entries with their dates, and
    // the open nights — never the AI's words, so it can claim nothing the log does not hold.
    changes: {
      none: 'لم يُسجَّل تغيير هذا الأسبوع',
      open: (n) => (n === 0 ? 'لا ليالٍ مفتوحة للبيع' : `${nightsCount(n)} مفتوحة للبيع من 7`)
    },
    pastResult: {
      lead: (date) => `الحكم على ما بعد تغيير ${date} محدود`,
      warnings: {
        most_after_nights_booked_before_change: 'معظم الليالي بعده كانت محجوزة قبله',
        one_booking_fills_most_of_a_period: 'حجز طويل واحد يملأ معظم إحدى الفترتين',
        nights_closed: 'كانت هناك ليالٍ مغلقة خلال الفترة',
        other_changes_overlap: 'تزامنت الفترة مع تغييرات أخرى',
        nights_without_calendar_record: 'بعض الليالي بلا بيانات تقويم'
      }
    }
  },
  // The card's system parts on a week judged with the similar-listings rule (layer-2 build step 5) — the code
  // reads them from the figures, never the AI.
  strip: {
    title: 'مقابل الوحدات المشابهة',
    systemTag: 'من أرقام النظام',
    pageViews: 'الدخول على صفحة الوحدة',
    bookingRate: 'نسبة الحجز (الأسبوع الماضي)',
    price: 'سعر الليلة',
    vs: 'مقابل',
    byAirbnb: 'حسب Airbnb',
    byMarket: 'مقارنة السوق',
    unavailable: 'غير متاح',
    limited: 'أدلة محدودة'
  },
  checks: {
    title: 'ما يُفحص',
    steps: {
      exposure: 'لا يظهر في البحث كثيراً',
      entry: 'يظهر في البحث ولا يُفتح',
      conversion: 'يُفتح ولا يُحجز',
      badge: 'فقد شارة «مفضلة لدى الضيوف»'
    },
    items: {
      coverPhoto: 'صورة الغلاف',
      title: 'العنوان',
      photos: 'الصور وترتيبها',
      descriptionAmenities: 'الوصف والمرافق',
      qualityPage: 'صفحة الجودة في Airbnb (تقييمات الفئات)',
      price: 'السعر',
      reviews: 'التقييمات',
      badge: 'شارة «مفضلة لدى الضيوف»',
      openNights: 'الليالي المفتوحة للبيع'
    },
    // Step 4: open nights on the exposure list — a mark, not a figure (the table holds the number; founder, no repeats).
    openFew: 'ليالٍ قليلة للبيع',
    onAirbnb: 'تحقق على Airbnb',
    notRead: 'غير مقروء — تحقق على Airbnb',
    priceNoComparison: 'لا مقارنة كافية — تحقق على Airbnb',
    noAverage: (count) => `${count} — لا يظهر متوسط التقييم على Airbnb قبل 3 تقييمات`,
    underFive: (count) => `${count} — أقل من 5 لا تؤهل لشارة «مفضلة لدى الضيوف»`,
    ratingLow: (rating, count) => `${rating} من 5 — ${count} — أقل من معيارنا 4.9`,
    ratingOk: (rating, count) => `${rating} من 5 — ${count}`,
    noReviews: 'لا تقييمات بعد',
    badgeOk: 'موجودة',
    badgeMissing: 'غير موجودة'
  },
  voice: {
    title: 'ما يزعج الضيوف',
    source: 'من «صوت الضيف»',
    // Why the block shows — said once, so ops know what the reviews are being read against.
    why: {
      badge_lost: 'لأن الوحدة فقدت شارة «مفضلة لدى الضيوف»',
      conversion: 'لأن الزوار يفتحون الصفحة ولا يحجزون'
    },
    none: 'لا شكاوى مسجّلة في تقييمات هذه الوحدة — السبب غالباً في مكان آخر',
    lastAt: 'آخر ذكر',
    repeat: 'متكرر',
    handled: 'قيد المتابعة',
    cameBack: 'عاد بعد الإصلاح',
    open: 'فتح «صوت الضيف»'
  },
  reviewedBy: 'راجعها',
  reasonShown: 'سبب عدم الاتفاق',
  notesCount: (n) => (n === 1 ? 'تنبيه واحد' : n === 2 ? 'تنبيهان' : `${n} تنبيهات`)
};

export const VERDICTS = {
  NOT_ON_AIRBNB: { label: 'ليست على Airbnb', tone: 'muted' },
  NO_DATA: { label: 'لا بيانات', tone: 'missing' },
  CALENDAR_NOT_KNOWN: { label: 'التقويم غير معروف', tone: 'missing' },
  CALENDAR_CLOSED: { label: 'التقويم مغلق', tone: 'neutral' },
  MOSTLY_BOOKED: { label: 'محجوز معظم الأسبوع', tone: 'neutral' },
  // Too few page views to judge the booking step — nothing to act on, not counted in «تحتاج مراجعة» (D5).
  INSUFFICIENT_EVIDENCE: { label: 'أدلة غير كافية', tone: 'neutral' },
  EXPOSURE_NOT_READ: { label: 'لم يُقرأ الظهور', tone: 'missing' },
  WEAK_EXPOSURE: { label: 'ضعف الظهور', tone: 'weak' },
  WEAK_ENTRY: { label: 'ضعف الدخول', tone: 'weak' },
  BOOKINGS_NOT_READ: { label: 'لم تُقرأ الحجوزات', tone: 'missing' },
  ENTRY_NOT_READ: { label: 'لم يُقرأ الدخول', tone: 'missing' },
  CONVERSION_NOT_READ: { label: 'لم يُقرأ الحجز بعد الدخول', tone: 'missing' },
  WEAK_CONVERSION: { label: 'ضعف التحويل', tone: 'weak' },
  LOW_SEARCH_AVAILABILITY: { label: 'توافر البحث منخفض', tone: 'weak' },
  HEALTHY: { label: 'سليم', tone: 'good' }
};

/** What each verdict means — the hover text, from the sheet's verdict key (Airbnb) and its
 *  Booking.com section, which judges against the competitive set instead of fixed thresholds. */
export const VERDICT_HELP_BOOKINGCOM = {
  NO_DATA: 'لا أرقام من Booking.com لهذه الفترة',
  EXPOSURE_NOT_READ: 'مشاهدات البحث أو مشاهدات المنافسين لم تُقرأ',
  ENTRY_NOT_READ: 'أرقام الدخول (أو أرقام يوم الأحد) لم تُقرأ كاملة',
  CONVERSION_NOT_READ: 'الحجز بعد الدخول أو رقم المنافسين لم يُقرأ',
  LOW_SEARCH_AVAILABILITY: 'توافر البحث تحت الحد',
  WEAK_EXPOSURE: 'مشاهدات البحث أقل من المنافسين بأكثر من الهامش',
  WEAK_ENTRY: 'نسبة الدخول أقل من المنافسين بأكثر من الهامش',
  WEAK_CONVERSION: 'الحجز بعد الدخول أقل من المنافسين بأكثر من الهامش',
  HEALTHY: 'مثل المنافسين أو أفضل في كل خطوة'
};

export const VERDICT_HELP = {
  NO_DATA: 'لا أرقام من المنصة لهذا الأسبوع',
  CALENDAR_NOT_KNOWN: 'ليالٍ بلا سجل في التقويم — لا يمكن الحكم إن كان مغلقاً أو محجوزاً',
  CALENDAR_CLOSED: 'ليلتان متاحتان أو أقل، ومعظم الباقي مغلق',
  MOSTLY_BOOKED: 'ليلتان متاحتان أو أقل، ومعظم الباقي محجوز — قلة الظهور متوقعة',
  WEAK_EXPOSURE: 'الظهور تحت الحد',
  WEAK_ENTRY: 'نسبة الدخول على صفحة الوحدة تحت الحد',
  WEAK_CONVERSION: 'ظهور ودخول جيدان بلا حجوزات',
  INSUFFICIENT_EVIDENCE: 'دخول قليل في آخر 4 أسابيع — لا يُحكم على الحجز',
  HEALTHY: 'حجز واحد على الأقل فوق الحدود',
  LOW_SEARCH_AVAILABILITY: 'توافر البحث تحت الحد'
};

export const COLLECTION = {
  received: { label: 'استُلم', icon: 'bi-check-circle' },
  missing: { label: 'ناقص', icon: 'bi-dash-circle' },
  failed: { label: 'تعذّرت القراءة', icon: 'bi-exclamation-circle' },
  not_applicable: { label: 'لا ينطبق', icon: 'bi-slash-circle' }
};

/** Unit-detail rows, in the sheet's order. `kind` decides the number format. */
export const DETAIL_ROWS = {
  impressions: { label: 'الظهور في الصفحة الأولى', kind: 'count' },
  pageViews: { label: 'الدخول على صفحة الوحدة', kind: 'count' },
  similarPageViews: { label: 'الدخول — الوحدات المشابهة', kind: 'count' },
  entryRate: { label: 'نسبة الدخول على صفحة الوحدة من مجموع الظهور %', kind: 'pct1' },
  overallBookingRate: { label: 'نسبة الحجز الإجمالية (Airbnb) %', kind: 'pct2' },
  similarBookingRate: { label: 'نسبة الحجز — الوحدات المشابهة %', kind: 'pct2' },
  newBookings: { label: 'حجوزات جديدة تمت هذا الأسبوع (Airbnb)', kind: 'count' },
  openNights: { label: 'الليالي المتاحة', kind: 'count' },
  rating: { label: 'التقييم', kind: 'rating' },
  reviewCount: { label: 'عدد التقييمات', kind: 'count' },
  badges: { label: 'الشارات', kind: 'text' },
  guestPrice: { label: 'سعر الضيف (ليلة واحدة)', kind: 'money' },
  bookedNightRate: { label: 'متوسط سعر الليلة المحجوزة', kind: 'money' },
  occupancy: { label: 'الإشغال % (كل المنصات)', kind: 'pct1' }
};

export const BDC_ROWS = {
  searchViews: { label: 'مشاهدات نتائج البحث', kind: 'count' },
  pageVisits: { label: 'الدخول على صفحة العقار', kind: 'count' },
  entryRate: { label: 'نسبة الدخول من مشاهدات البحث %', kind: 'pct2' },
  entryRateWithoutSundays: { label: 'نسبة الدخول — بدون يوم الأحد %', kind: 'pct2' },
  sundayVisits: { label: 'زيارات يوم الأحد', kind: 'count' },
  bookings: { label: 'الحجوزات (تشمل الملغاة)', kind: 'count' },
  keptBookings: { label: 'الحجوزات الباقية', kind: 'count' },
  bookingAfterVisitRate: { label: 'نسبة الحجز بعد الدخول %', kind: 'pct2' },
  searchAvailability: { label: 'توافر البحث %', kind: 'pct1' },
  cancellationRate: { label: 'نسبة الإلغاء % (365 يوماً)', kind: 'pct1' },
  bookedNightRate: { label: 'متوسط سعر الليلة المحجوزة', kind: 'money' },
  unitBookedNightRate: { label: 'متوسط سعر الليلة المحجوزة —', kind: 'money' },
  rating: { label: 'التقييم (من 10)', kind: 'rating' },
  reviewCount: { label: 'عدد التقييمات', kind: 'count' }
};

export const VERSUS = {
  AT_OR_ABOVE: { label: 'مثل المنافسين أو أفضل', tone: 'good' },
  BELOW: { label: 'أضعف من المنافسين', tone: 'weak' },
  LOWER_OR_EQUAL: { label: 'إلغاء أقل من المنافسين', tone: 'good' },
  HIGHER: { label: 'إلغاء أعلى من المنافسين', tone: 'weak' }
};

/** «تقييم واحد» · «تقييمان» · «3 تقييمات» · «11 تقييماً» — Arabic number agreement for reviews. */
export const reviewsCountText = (n) => {
  if (n === 1) return 'تقييم واحد';
  if (n === 2) return 'تقييمان';
  return `${n} ${n >= 3 && n <= 10 ? 'تقييمات' : 'تقييماً'}`;
};

/** «ليلة واحدة» · «ليلتان» · «3 ليالٍ» · «11 ليلة» — Arabic number agreement for nights. */
const nights = (n, fmt) => {
  if (n === 1) return 'ليلة واحدة';
  if (n === 2) return 'ليلتان';
  return `${fmt(n)} ${n >= 3 && n <= 10 ? 'ليالٍ' : 'ليلة'}`;
};

/** «وحدة واحدة» · «وحدتان» · «3 وحدات» · «11 وحدة» — Arabic number agreement for similar units. */
const unitsCount = (n, fmt) => {
  if (n === 1) return 'وحدة واحدة';
  if (n === 2) return 'وحدتان';
  return `${fmt(n)} ${n >= 3 && n <= 10 ? 'وحدات' : 'وحدة'}`;
};

/**
 * «مقابل السوق» — the market price comparison (tasks/market-price-comparison-build.md step 6). The backend sends
 * codes (services/marketPrice: the unit statuses and reason codes); these are their words. An INDICATION, never a
 * target price (founder, 22 Oct): neutral ink, no «should», no colour for above / below.
 * marketVocabularyParity.test.js (backend) holds `status` and `hint` equal to the backend's codes.
 */
export const MARKET = {
  head: 'مقابل السوق',
  headHelp: 'مقارنة بمتوسط سعر السوق: سعر الضيف لليلة، لإقامة ليلة أو ليلتين، مقارنة بوحدات مشابهة قريبة على Airbnb، للأسبوعين التاليين لنهاية الأسبوع (الأسعار المعروضة، لا المحجوزة). مؤشر للمراجعة، لا سعر مقترح. «سعر الليلة» بجانبه رقم مختلف: متوسط ما حُجز هذا الأسبوع.',
  above: 'أعلى',
  below: 'أقل',
  // Statuses shown as a word (above / below show the word above with the figure).
  status: {
    at: 'ضمن نطاق السوق',
    insufficient: 'غير كافٍ',
    uncertain: 'غير مؤكد',
    no_open_nights: 'لا ليالٍ متاحة',
    not_on_airbnb: 'ليست على Airbnb',
    no_group: 'بلا مقارنة'
  },
  // Why there is no figure — keyed by the reason code, or by the status when it has none.
  hint: {
    insufficient_comps: 'الوحدات المشابهة المتاحة لإقامة ليلة أو ليلتين أقل من الحد الأدنى في كل الليالي المتاحة.',
    vat_uncertain: 'حالة التسجيل الضريبي لحساب Airbnb غير مسجّلة في «حسابات المنصات والتسجيل الضريبي»، أو لا تطابق الحجوزات الأخيرة (انظر تنبيه الضريبة) — بدونها قد يختلف سعرنا 15%.',
    no_price_data: 'لم تصل أسعار الوحدة هذا الأسبوع.',
    min_stay: 'لياليها المتاحة لا تقبل إقامة ليلة أو ليلتين، والمقارنة لهذه الإقامات فقط.',
    vendor_error: 'تعذّرت قراءة أسعار الوحدة هذا الأسبوع.',
    no_open_nights: 'لا ليالٍ متاحة في الأسبوعين (محجوزة أو مغلقة) — لا شيء للمقارنة.',
    not_on_airbnb: 'لا يوجد رقم إعلان Airbnb للوحدة في النظام.',
    no_group: 'الوحدة ليست ضمن مجموعة وحدات مشابهة معتمدة.'
  },
  noRun: 'لا توجد مقارنة لهذا الأسبوع.',
  limited: 'أدلة محدودة',
  priceRow: 'متوسط سعر السوق (وحدات مشابهة)',
  gapRow: 'مقارنة بمتوسط سعر السوق',
  // The group's approved competitors, folded under the two rows — each opens its listing on Airbnb.
  showCompetitors: (n) => `عرض الوحدات المشابهة (${n})`,
  hideCompetitors: 'إخفاء الوحدات المشابهة',
  competitorsHint: 'الوحدات المعتمدة للمقارنة — افتح أي إعلان على Airbnb لترى الصور والسعر والتقييمات',
  openOnAirbnb: 'افتح الإعلان على Airbnb'
};

/** «ضيفان» · «4 ضيوف» · «11 ضيفاً» — Arabic number agreement for guests. */
export const guestsText = (n) => {
  if (n === 1) return 'ضيف واحد';
  if (n === 2) return 'ضيفان';
  return `${n} ${n >= 3 && n <= 10 ? 'ضيوف' : 'ضيفاً'}`;
};

/** The evidence behind a verdict, in a sentence — the cell's hover text. */
export const marketEvidenceText = (c, fmt) => {
  const parts = [
    `سعرنا ${fmt(c.ourPrice)} ر.س، والوحدات المشابهة ${fmt(c.marketMedian)} ر.س (${marketRangeText(c, fmt)}).`,
    `قورنت ${nights(c.compared, fmt)} من ${fmt(c.nights)}، مع ${unitsCount(c.competitorsMin, fmt)} مشابهة على الأقل.`
  ];
  const weak = [c.indicative && 'أقل من 5 وحدات مشابهة', c.limitedEvidence && 'أقل من 3 ليالٍ قورنت'].filter(Boolean);
  if (weak.length) parts.push(`${MARKET.limited}: ${weak.join('، ')}.`);
  return parts.join(' ');
};

/**
 * The reading's market price line (brief-v11) — the same words as the page's «مقابل السوق» column, written from the
 * system's figures: «أعلى 18.1% (342 مقابل 290 ر.س، 8 ليالٍ من 14)», or the reason when there is no verdict.
 */
export const marketPriceSentence = (p, { money, pct }) => {
  if (typeof p.gapPct === 'number') {
    const word = p.status === 'above' ? `${MARKET.above} ${pct(Math.abs(p.gapPct))}`
      : p.status === 'below' ? `${MARKET.below} ${pct(Math.abs(p.gapPct))}` : MARKET.status.at;
    const detail = `${money(p.ourPrice)} مقابل ${money(p.marketMedian)} ر.س، ${nights(p.compared, money)} من ${money(p.nights)}`;
    return `${word} (${detail})${p.limitedEvidence ? ` — ${MARKET.limited}` : ''}`;
  }
  if (p.status === 'no_comparison') return MARKET.noRun;
  return `${MARKET.status[p.status] || p.status} — ${MARKET.hint[p.reasonCode || p.status] || ''}`;
};

/** «بين 272 و311» — the similar units' middle range, in words: a dash between two numbers breaks in RTL text. */
export const marketRangeText = (c, fmt) => `بين ${fmt(c.marketLow)} و${fmt(c.marketHigh)}`;

/**
 * «5 من 8 وحدات» — at least 5 of the group's 8 approved units could take a 1–2 night stay on each night compared (the
 * fewest on any night, of the group's size). The group's size is missing on runs saved before it was shown: then «5 وحدات».
 */
export const marketUnitsText = (n, fmt, listed = null) => (typeof listed === 'number' && listed > 0
  ? `${fmt(n)} من ${unitsCount(listed, fmt)}`
  : unitsCount(n, fmt));

/** The market figure's hover — what «متوسط» means here, and the count behind it. */
export const marketPriceHint = (n, fmt) => `السعر الأوسط للوحدات المشابهة، شامل التنظيف والضريبة — ${unitsCount(n, fmt)} على الأقل في كل ليلة قورنت.`;

/**
 * The 4-week mean's hover, in words — «متوسط 3 أسابيع: أقل 3.9%». A leading +/− sign in RTL hover text jumps to
 * the wrong side, like the dash did.
 */
export const marketAverageText = (weeks, gapPct, pct) => {
  const span = weeks === 1 ? 'أسبوع واحد' : weeks === 2 ? 'أسبوعين' : `${weeks} أسابيع`;
  const word = gapPct > 0 ? MARKET.above : gapPct < 0 ? MARKET.below : '';
  return `متوسط ${span}: ${word ? `${word} ${pct(Math.abs(gapPct))}` : pct(0)}`;
};

/** Which rule fired, in a sentence — the chip's hover text and the detail's «لماذا» line. */
export const verdictReason = (code, inputs = {}, fmt = (v) => v) => {
  const i = inputs || {};
  // The booking step judged over 4 weeks (layer-2 rule, founder + consultant 23 Oct): each reason = the fact + where
  // to look (founder 23 Oct — a count alone tells ops nothing; what it rules out does).
  if ('bookings4w' in i) {
    switch (code) {
      case 'WEAK_CONVERSION':
        return `${fmt(i.pageViews4w)} دخول في آخر 4 أسابيع بلا حجز — يفتحون الصفحة ولا يحجزون: افحص السعر والصور والتقييمات`;
      case 'INSUFFICIENT_EVIDENCE':
        return i.pageViews4w === null || i.pageViews4w === undefined
          ? 'الدخول في آخر 4 أسابيع غير مقروء — لا يُحكم على الحجز بعد'
          : `دخول قليل (${fmt(i.pageViews4w)} في آخر 4 أسابيع) — لا يُحكم على الحجز بعد`;
      // A few bookings are a good sign, not proof the listing converts well (consultant 23 Oct).
      case 'HEALTHY': return `الحجوزات تصل (${fmt(i.bookings4w)} في آخر 4 أسابيع) — لا يظهر ضعف في الإعلان`;
      default: break;
    }
  }
  // Judged against Airbnb's similar listings (layer-2 rule) — the page views lead, and the sentence says which
  // way they compare («أعلى من / أقل من»), never two bare numbers side by side (founder, 23 Oct).
  if (i.basis === 'similar') {
    const side = i.pageViews > i.similarPageViews ? 'أعلى من' : i.pageViews === i.similarPageViews ? 'مثل' : 'أقل من';
    const vs = `الدخول ${fmt(i.pageViews)} — ${side} الوحدات المشابهة (${fmt(i.similarPageViews)})`;
    switch (code) {
      case 'WEAK_EXPOSURE': return `${vs}، والظهور ${fmt(i.impressions)} أقل من الحد ${fmt(i.threshold)}`;
      case 'WEAK_ENTRY': return `${vs} رغم ظهور ${fmt(i.impressions)} فوق الحد`;
      case 'WEAK_CONVERSION': return `${vs} — لكن بلا أي حجز جديد على Airbnb`;
      case 'HEALTHY': return `${fmt(i.newBookings)} حجز جديد — ${vs}`;
      default: break;
    }
  }
  switch (code) {
    case 'WEAK_EXPOSURE': return `الظهور ${fmt(i.impressions)} أقل من الحد ${fmt(i.threshold)}`;
    case 'WEAK_ENTRY': return `نسبة الدخول ${fmt(i.entryRate)}% أقل من الحد ${fmt(i.threshold)}%`;
    case 'WEAK_CONVERSION': return `ظهور ${fmt(i.impressions)} ونسبة دخول ${fmt(i.entryRate)}% بلا أي حجز جديد على Airbnb`;
    case 'HEALTHY': return `${fmt(i.newBookings)} حجز جديد، والظهور والدخول فوق الحدود`;
    case 'MOSTLY_BOOKED': return `${i.open === 0 ? 'لا ليالٍ متاحة' : `${nights(i.open, fmt)} متاحة فقط`}، و${nights(i.booked, fmt)} محجوزة — قلة الظهور متوقعة`;
    case 'CALENDAR_CLOSED': return `${i.open === 0 ? 'لا ليالٍ متاحة' : `${nights(i.open, fmt)} متاحة فقط`}، ومعظم الباقي مغلق`;
    case 'CALENDAR_NOT_KNOWN': return `${nights(i.unknown, fmt)} بلا سجل في التقويم — لا يمكن الحكم`;
    case 'NO_DATA': return i.failed ? 'تعذّرت قراءة المنصة هذا الأسبوع' : VERDICT_HELP.NO_DATA;
    default: return VERDICT_HELP[code] || '';
  }
};

/** Glance-strip order: what needs a look first. */
export const VERDICT_ORDER = [
  'WEAK_EXPOSURE', 'WEAK_ENTRY', 'WEAK_CONVERSION', 'LOW_SEARCH_AVAILABILITY',
  'NO_DATA', 'CALENDAR_NOT_KNOWN', 'EXPOSURE_NOT_READ', 'ENTRY_NOT_READ', 'BOOKINGS_NOT_READ', 'CONVERSION_NOT_READ',
  'CALENDAR_CLOSED', 'MOSTLY_BOOKED', 'INSUFFICIENT_EVIDENCE', 'HEALTHY', 'NOT_ON_AIRBNB'
];

/** A threshold as people read it: «60%» for the percent ones, a plain number otherwise. */
const PERCENT_THRESHOLDS = new Set(['airbnbEntryRateMin', 'occupancyTarget', 'bookingcomSearchAvailabilityMin']);
export const thresholdValueText = (field, v) => (v === null || v === undefined
  ? TEXT.thresholdOff
  : `${v}${PERCENT_THRESHOLDS.has(field) ? '%' : ''}`);

/** «أقل من المنافسين بـ 12%» */
export const priceVersusText = (v) => (v ? `${v.direction === 'LOWER' ? 'أقل' : 'أعلى'} من المنافسين بـ ${v.pct}%` : '');

export const TREND = {
  up: { glyph: '↑', label: 'ارتفع' },
  down: { glyph: '↓', label: 'انخفض' },
  same: { glyph: '=', label: 'بدون تغيير' },
  changed: { glyph: 'تغيّر', label: 'تغيّر' }
};

const ERROR_TEXT = {
  [C.KEY_NAME_REQUIRED]: 'اكتب اسماً للمفتاح.',
  [C.KEY_NAME_TOO_LONG]: 'اسم المفتاح طويل جداً.',
  [C.KEY_HOLDER_INVALID]: 'المفتاح يُصدر لموظف عمليات نشط فقط.',
  [C.KEY_NOT_FOUND]: 'المفتاح غير موجود.',
  [C.WEEK_INVALID]: 'الأسبوع غير صالح.',
  [C.VIEW_INVALID]: 'طريقة العرض غير صالحة.',
  [C.OWNER_REQUIRED]: 'اختر المالك أولاً لعرض صفحة المالك.',
  [C.OWNER_INVALID]: 'المالك غير صالح.',
  [C.UNIT_NOT_FOUND]: 'الوحدة غير موجودة.',
  [C.CHANGE_DATE_INVALID]: 'التاريخ غير صالح.',
  [C.CHANGE_TEXT_INVALID]: 'نص غير صالح.',
  [C.CHANGE_TEXT_TOO_LONG]: 'النص طويل جداً.',
  [C.CHANGE_REQUIRED]: 'اكتب التغيير.',
  [C.CHANGE_NOT_FOUND]: 'هذا السطر لم يعد موجوداً.',
  [C.CHANGE_PLATFORM_REQUIRED]: 'اختر المنصة التي طُبّق عليها التغيير.',
  [C.CHANGE_PLATFORM_INVALID]: 'منصة غير صالحة.',
  [C.THRESHOLD_INVALID]: 'القيم غير مقروءة.',
  [C.THRESHOLD_OUT_OF_RANGE]: 'إحدى القيم خارج الحدود المسموحة.',
  [C.THRESHOLD_REASON_REQUIRED]: 'اذكر سبب التغيير.',
  [C.THRESHOLD_REASON_TOO_LONG]: 'السبب طويل جداً.',
  [C.THRESHOLD_NO_CHANGE]: 'لم تتغير أي قيمة.',
  [C.BRIEF_DISABLED]: 'قراءة المساعد الذكي متوقفة حالياً.',
  [C.BRIEF_NOT_FOUND]: 'لا توجد قراءة لهذا الأسبوع.',
  [C.BRIEF_REVIEW_INVALID]: 'اختر «أتفق» أو «لا أتفق».',
  [C.BRIEF_REASON_TOO_LONG]: 'السبب طويل جداً.',
  [C.BRIEF_NOT_REVIEWABLE]: 'لا توجد قراءة جاهزة للمراجعة.',
  [C.BRIEF_WEEK_NOT_CURRENT]: 'إعادة القراءة لآخر أسبوع مكتمل فقط.',
  [C.BRIEF_NOT_NEEDED]: 'الوحدة لا تحتاج مراجعة هذا الأسبوع.',
  [C.BRIEF_RERUN_NOT_ALLOWED]: 'للوحدة قراءة لهذا الأسبوع.',
  [C.BRIEF_CHANGED]: 'تحدّثت القراءة — أعد تحميل الصفحة.',
  [C.BRIEF_ATTEMPTS_USED]: 'استُنفدت محاولات القراءة لأرقام هذا الأسبوع.'
};

// The AI's text sometimes carries a raw name from its data («airbnbMarketPrice») — meaningless to ops. The page swaps
// each for its Arabic label before showing it (step 4, founder 3 Nov); a mechanical swap, never the AI's.
const AI_FIELD_LABELS = {
  airbnbMarketPriceAtWeekStart: 'مقارنة السعر في بداية الأسبوع',
  airbnbMarketPrice: 'مقارنة السعر بالوحدات المشابهة',
  bookedNightRate: 'متوسط سعر الليلة المحجوزة',
  overallBookingRate: 'نسبة الحجز الإجمالية',
  similarBookingRate: 'نسبة الحجز للوحدات المشابهة',
  similarListingsPageViews: 'الدخول على صفحة الوحدات المشابهة',
  overallListingRating: 'التقييم العام',
  totalReviewCount: 'عدد التقييمات',
  nightsSoldBookedThisWeek: 'الليالي المحجوزة خلال الأسبوع',
  nightsSold: 'الليالي المباعة',
  thisWeekNights: 'ليالي الأسبوع',
  ratingVsStandard: 'التقييم مقابل معيارنا',
  ratingStandard: 'معيار التقييم',
  dataConflicts: 'تعارض البيانات',
  marketSignals: 'إشارات السوق',
  bookingEvidence4w: 'أدلة الحجز خلال 4 أسابيع',
  airbnbDiscounts: 'خصومات Airbnb',
  changesThisWeek: 'تغييرات هذا الأسبوع',
  changesEarlier: 'التغييرات السابقة',
  occupancyByWeek: 'الإشغال الأسبوعي',
  reviewsByWeek: 'التقييمات الأسبوعية',
  entryRate: 'نسبة الدخول',
  pageViews: 'الدخول على صفحة الوحدة',
  impressions: 'الظهور',
  openNights: 'الليالي المفتوحة',
  guestPrice: 'سعر الضيف',
  newBookings: 'الحجوزات الجديدة',
  usableForPrice: 'صلاحية المقارنة',
  gapPct: 'فرق السعر',
  occupancy: 'الإشغال',
  occupancyTargetPct: 'هدف الإشغال',
  funnelNotes: 'ملاحظات الأرقام',
  nightsFreedLateByCancellation: 'ليالٍ أُلغيت متأخراً',
  outcome: 'نتيجة التغيير',
  // The system's codes (audit, 3 Nov) — the warnings reuse the card's own words.
  ...BRIEF.facts.pastResult.warnings,
  airbnb_booking_rate_zero_with_bookings: 'تعارض نسبة الحجز في Airbnb مع الحجوزات المسجّلة',
  sold_nights_differ_from_weekly_record: 'تعارض الليالي المباعة مع السجل الأسبوعي',
  open_rate_down_only_from_more_impressions: 'انخفاض نسبة الدخول بسبب زيادة الظهور',
  no_reviews: 'لا تقييمات بعد',
  too_few_reviews: 'تقييمات قليلة'
};
// A raw token: camelCase, a dotted path (thisWeek.occupancy.current), or a snake_case code.
const RAW_TOKEN = /\b[A-Za-z][A-Za-z0-9]*(?:\.[A-Za-z0-9]+)+\b|\b[a-z]+[A-Z][A-Za-z0-9]*\b|\b[a-z]+(?:_[a-z0-9]+)+\b/g;
export const plainFieldNames = (text) => String(text || '').replace(RAW_TOKEN, (token) => {
  const known = token.split('.').reverse().find(part => AI_FIELD_LABELS[part]);
  return known ? AI_FIELD_LABELS[known] : token;
});

export const errorText = (err) => {
  const data = err?.response?.data;
  if (data?.code && ERROR_TEXT[data.code]) return ERROR_TEXT[data.code];
  return data?.error || TEXT.unexpected;
};

// Counts for the box on a change, Western digits as on the rest of this page. Only for a count above zero.
export const nightsCount = (n) => nights(n, String);
/** «حجز واحد» · «حجزان» · «3 حجوزات» · «11 حجزاً» — the same agreement as `nights`. */
export const bookingsCount = (n) => {
  if (n === 1) return 'حجز واحد';
  if (n === 2) return 'حجزان';
  return `${n} ${n <= 10 ? 'حجوزات' : 'حجزاً'}`;
};
